import { useRoles } from '@/hooks/useRoles';
import { useAppStore } from '@/stores/app';
import type { Org } from '@/types';
import ErrorMessage from '@components/ErrorMessage';
import { Button } from '@nautilusbio/nb-design-system/Button';
import Loading from '@nautilusbio/nb-design-system/Loading';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import clsx from 'clsx';
import React, { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { SIDEBAR_ICON_CONFIG, sidebarTextSize } from '../../constants';
import styles from '../../styles.module.css';

import {
  CONTAINER_ID,
  OrgIcon,
  POPOVER_OFFSET_PX,
  SIDEBAR_COLLAPSED_POPOVER_POSITIONS,
  SIDEBAR_POPOVER_POSITIONS,
} from './constants';
import { ALL_ROLES, useOrganizations, useRoleSwitcher } from './hooks';
import { OrgSelectorContent } from './OrgSelectorContent';

interface OrgSelectorProps {
  selectedOrg: Org;
}

/**
 * OrgSelector - Main component for selecting and switching organizations
 */
const OrgSelector: FC<OrgSelectorProps> = ({ selectedOrg }) => {
  const { t: tOrgs } = useTranslation('orgs');

  const { isOrgSelectorOpen, isSidebarCollapsed, setIsOrgSelectorOpen, setIsSidebarMenuOpen } = useAppStore(
    useShallow(state => ({
      isOrgSelectorOpen: state.isOrgSelectorOpen,
      isSidebarCollapsed: state.isSidebarCollapsed,
      setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
      setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
    })),
  );

  const { allowedRolesByOrg, assumedRolesByOrg, isGlobalSuperAdmin } = useRoles();

  const { data: organizations, isLoading, error } = useOrganizations();

  // Stable identity: handleSwitchRole depends on it, and it reaches the memoised OrgListItem as
  // onSwitch — a fresh arrow here re-renders every row on every render of this component.
  const closeSelector = useCallback(() => setIsOrgSelectorOpen(false), [setIsOrgSelectorOpen]);

  const { handleSwitchRole, isAssumingRole } = useRoleSwitcher(selectedOrg, closeSelector);

  const isSuperAdmin = useMemo(() => isGlobalSuperAdmin(), [isGlobalSuperAdmin]);

  // For super admins, provide all roles for every org in the list
  const effectiveAllowedRolesByOrg = useMemo(() => {
    if (!isSuperAdmin) return allowedRolesByOrg;
    return Object.fromEntries((organizations ?? []).map(org => [org.id, ALL_ROLES]));
  }, [isSuperAdmin, allowedRolesByOrg, organizations]);

  const { size, strokeWidth } = isSidebarCollapsed ? SIDEBAR_ICON_CONFIG.collapsed : SIDEBAR_ICON_CONFIG.expanded;

  const handleToggle = () => {
    setIsOrgSelectorOpen(!isOrgSelectorOpen);
    if (!isSidebarCollapsed) setIsSidebarMenuOpen(false);
  };

  const handleClose = () => {
    setIsOrgSelectorOpen(false);
    if (isSidebarCollapsed) setIsSidebarMenuOpen(false);
  };

  if (isLoading) {
    return <Loading className={styles.loading} label={tOrgs('org_loading')} type="inline" />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!organizations || organizations.length === 0 || !selectedOrg) {
    return null;
  }

  const popoverContent = (
    <Stack
      className={clsx(styles.orgSelectorContainer, styles.popoverContainerWidth)}
      gap={1}
      id={CONTAINER_ID}
      orientation="vertical"
    >
      <OrgSelectorContent
        allowedRolesByOrg={effectiveAllowedRolesByOrg}
        assumedRolesByOrg={assumedRolesByOrg}
        isPending={isAssumingRole}
        onSwitch={handleSwitchRole}
        orgs={organizations}
        selectedOrg={selectedOrg}
        tOrgs={tOrgs}
      />
    </Stack>
  );

  return (
    <Popover
      // 'right' alone still centres the flyout on the trigger, so a tall list overhangs upward into
      // the nav links. 'start' pins its top edge to the trigger row, which is what the mock shows.
      align="start"
      data-testid="org-selector-popover"
      className={styles.popoverContent}
      content={popoverContent}
      isOpen={isOrgSelectorOpen}
      onClickOutside={handleClose}
      padding={POPOVER_OFFSET_PX}
      positions={isSidebarCollapsed ? SIDEBAR_COLLAPSED_POPOVER_POSITIONS : SIDEBAR_POPOVER_POSITIONS}
      styled
    >
      <Stack className={styles.flyoutTriggerWrapper}>
        <Button
          aria-expanded={isOrgSelectorOpen}
          // The icon contributes no accessible name, so without this the button announces just
          // the org name — a proper noun with no hint that it switches organizations.
          aria-label={tOrgs('current_organization', { name: selectedOrg.name })}
          className={styles.sidebarLink}
          data-testid="sidebar-org-selector-trigger"
          fullWidth
          onPress={handleToggle}
          visual="unstyled"
        >
          <OrgIcon
            size={size}
            strokeWidth={strokeWidth}
            className={isSidebarCollapsed ? styles.iconIsCollapsed : undefined}
          />

          <Text as="span" className={styles.footerButtonText} noWrap size={sidebarTextSize}>
            {selectedOrg.name}
          </Text>
        </Button>
      </Stack>
    </Popover>
  );
};

export default OrgSelector;
